<template>
  <v-container fluid class="priority-listings-page pa-6">
    <v-row>
      <v-col cols="12">
        <v-sheet class="hero-panel pa-6" rounded="lg">
          <div class="hero-header">
            <div>
              <p class="hero-kicker mb-1">Admin Workspace</p>
              <h2 class="hero-title mb-2">Priority Listings</h2>
              <p class="hero-subtitle mb-0">
                Manage premium listing placements, campaigns and renewal windows from one dashboard.
              </p>
            </div>
            <div class="hero-actions">
              <v-btn color="white" class="mr-3 hero-filter-btn" @click="toggleAdvanceSearch">
                <v-icon left>mdi-tune-variant</v-icon>
                {{ filtersOpen ? 'Hide Filters' : 'Show Filters' }}
              </v-btn>
              <v-btn
                v-if="modelPermissions.create"
                color="secondary"
                dark
                class="hero-create-btn"
                @click="openCreateDialog"
              >
                <v-icon left>mdi-plus</v-icon>
                Create Listing
              </v-btn>
            </div>
          </div>

          <div class="hero-metrics mt-5">
            <div class="metric-card">
              <p class="metric-label mb-1">Total Records</p>
              <p class="metric-value mb-0">{{ totalRecords }}</p>
            </div>
            <div class="metric-card">
              <p class="metric-label mb-1">Rows Loaded</p>
              <p class="metric-value mb-0">{{ loadedRecords }}</p>
            </div>
            <div class="metric-card">
              <p class="metric-label mb-1">Filters Active</p>
              <p class="metric-value mb-0">{{ hasActiveFilters ? 'Yes' : 'No' }}</p>
            </div>
          </div>
        </v-sheet>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-expand-transition>
          <v-card v-show="filtersOpen" class="filters-card mb-5" outlined>
            <v-card-text class="pb-2">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model.lazy="search"
                    clearable
                    clear-icon="mdi-close-circle"
                    prepend-inner-icon="mdi-magnify"
                    label="Search Name or Email"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <date-picker v-model="fromDate" label="From"></date-picker>
                </v-col>
                <v-col cols="12" md="4">
                  <date-picker v-model="toDate" label="To"></date-picker>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12" md="4">
                  <select-categories
                    v-model="category"
                    label="Category"
                  ></select-categories>
                </v-col>
                <v-col cols="12" md="4">
                  <select-suburbs
                    v-model="suburb"
                    label="Suburb"
                  ></select-suburbs>
                </v-col>
                <v-col cols="12" md="4" class="d-flex align-center justify-end">
                  <v-btn
                    outlined
                    color="primary"
                    :disabled="!hasActiveFilters"
                    @click="clearFilters"
                  >
                    <v-icon left>mdi-filter-remove</v-icon>
                    Clear Filters
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-expand-transition>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="table-card" outlined>
          <v-data-table
            :server-items-length="priority_listings.total"
            :loading="loading"
            :headers="headers"
            :items="priority_listings.data"
            :options.sync="options"
            :items-per-page="5"
            class="priority-listings-table"
            loading-text="Loading priority listings..."
          >
            <template v-slot:item.name="{ item }">
              <div class="name-cell">
                <v-avatar color="#ede9fe" size="34">
                  <v-icon small color="#5b21b6">mdi-star-four-points-outline</v-icon>
                </v-avatar>
                <div class="ml-3">
                  <p class="name-main mb-0">{{ item.name || '-' }}</p>
                  <p class="name-sub mb-0">Listing ID: {{ item.id }}</p>
                </div>
              </div>
            </template>

            <template v-slot:item.email="{ item }">
              <a
                v-if="item.email"
                :href="`mailto:${item.email}`"
                class="email-link"
              >
                <v-icon x-small class="mr-1">mdi-email</v-icon>
                {{ item.email }}
              </a>
              <span v-else class="grey--text text--darken-1">No email</span>
            </template>

            <template v-slot:item.text_messages_count="{ item }">
              <v-chip small color="#ede9fe" text-color="#5b21b6">
                {{ item.text_messages_count || 0 }} sent
              </v-chip>
            </template>

            <template v-slot:item.verified="{ item }">
              <v-chip
                small
                :color="item.verified === 1 ? '#dcfce7' : '#fee2e2'"
                :text-color="item.verified === 1 ? '#166534' : '#991b1b'"
              >
                {{ item.verified === 1 ? 'Verified' : 'Unverified' }}
              </v-chip>
            </template>

            <template v-slot:item.active="{ item }">
              <v-chip
                small
                :color="item.active === 1 ? '#dbeafe' : '#f3f4f6'"
                :text-color="item.active === 1 ? '#1e3a8a' : '#4b5563'"
              >
                {{ item.active === 1 ? 'Active' : 'Inactive' }}
              </v-chip>
            </template>

            <template v-slot:item.start_date="{ item }">
              <v-chip small outlined color="#7c3aed">
                {{ item.start_date || 'Not set' }}
              </v-chip>
            </template>

            <template v-slot:item.end_date="{ item }">
              <v-chip small outlined color="#c2410c">
                {{ item.end_date || 'Not set' }}
              </v-chip>
            </template>

            <template v-if="modelPermissions.edit" v-slot:item.edit="{ item }">
              <v-btn icon color="primary" @click="editItem(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>

            <template v-if="modelPermissions.delete" v-slot:item.delete="{ item }">
              <v-btn icon color="error" @click="deleteItem(item)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>

            <template v-slot:no-data>
              <div class="py-8 text-center">
                <v-icon color="grey" large class="mb-2">mdi-database-off-outline</v-icon>
                <p class="mb-0 grey--text text--darken-1">No priority listings found.</p>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <v-dialog persistent v-model="dialog">
      <priority-listing-dialog
        v-if="dialog"
        :priority_listings="priority_listings"
        :item="item"
        :close="close"
      ></priority-listing-dialog>
    </v-dialog>
  </v-container>
</template>

<script>
import PriorityListingDialog from './PriorityListingDialog'
import DatePicker from '../../../components/DatePicker'
import SelectSuburbs from '../../../components/SelectSuburbs'
import SelectCategories from '../../../components/SelectCategories'
import axios from '../../../utils/axios'
import { mapGetters } from 'vuex'

export default {
  name: 'PriorityListingIndex',
  components: {
    PriorityListingDialog,
    DatePicker,
    SelectCategories,
    SelectSuburbs
  },
  data() {
    return {
      filtersOpen: true,
      dialog: false,
      loading: false,
      headers: [
        {
          text: 'Name',
          align: 'start',
          value: 'name'
        },
        {
          text: 'Email',
          align: 'start',
          value: 'email'
        },
        {
          text: 'Messages Sent',
          align: 'start',
          value: 'text_messages_count'
        },
        {
          text: 'Verified',
          align: 'center',
          value: 'verified',
          sortable: false
        },
        { text: 'Status', align: 'center', value: 'active', sortable: false },
        { text: 'Start Date', align: 'center', value: 'start_date', sortable: false },
        { text: 'End Date', align: 'center', value: 'end_date', sortable: false },
        { text: 'Edit', align: 'center', value: 'edit', sortable: false },
        { text: 'Delete', align: 'center', value: 'delete', sortable: false }
      ],
      priority_listings: {
        data: [],
        total: 0
      },
      options: {},
      item: {},
      search: '',
      fromDate: '',
      toDate: '',
      category: '',
      suburb: ''
    }
  },
  computed: {
    ...mapGetters(['permissions/getModelPermissions']),
    modelPermissions() {
      return this['permissions/getModelPermissions']('priority_listing')
    },
    totalRecords() {
      return this.priority_listings && this.priority_listings.total
        ? this.priority_listings.total
        : 0
    },
    loadedRecords() {
      return this.priority_listings &&
        this.priority_listings.data &&
        this.priority_listings.data.length
        ? this.priority_listings.data.length
        : 0
    },
    hasActiveFilters() {
      return (
        this.search !== '' ||
        this.fromDate !== '' ||
        this.toDate !== '' ||
        this.category !== '' ||
        this.suburb !== ''
      )
    }
  },
  methods: {
    toggleAdvanceSearch() {
      this.filtersOpen = !this.filtersOpen
    },
    clearFilters() {
      this.search = ''
      this.fromDate = ''
      this.toDate = ''
      this.category = ''
      this.suburb = ''
    },
    openCreateDialog() {
      this.item = {}
      this.dialog = true
    },
    close() {
      this.item = {}
      this.dialog = false
    },
    editItem(item) {
      this.item = item
      this.dialog = true
    },
    getPriorityListings() {
      const page = this.options.page || 1
      const itemsPerPage = this.options.itemsPerPage || 5
      this.loading = true
      axios
        .get(
          encodeURI(
            `api/admin/priority-listings?page=${page}&itemsPerPage=${itemsPerPage}&search=${this.search}&category=${this.category}&suburb=${this.suburb}&fromDate=${this.fromDate}&endDate=${this.toDate}`
          )
        )
        .then(res => {
          this.priority_listings = res.data
        })
        .catch(() => {
          this.priority_listings = { data: [], total: 0 }
        })
        .finally(() => {
          this.loading = false
        })
    },
    deleteItem(item) {
      if (confirm(`Confirm Delete...${item.id}`)) {
        axios
          .delete(`api/admin/priority-listings/${item.id}`)
          .then(res => {
            if (res.data === 1) {
              this.getPriorityListings()
            }
          })
          .catch(() => {})
      }
    }
  },
  watch: {
    options: {
      handler() {
        this.getPriorityListings()
      },
      deep: true
    },
    dialog: {
      handler() {
        this.getPriorityListings()
      },
      deep: true
    },
    search: {
      handler() {
        this.getPriorityListings()
      },
      deep: true
    },
    fromDate: {
      handler() {
        this.getPriorityListings()
      },
      deep: true
    },
    toDate: {
      handler() {
        this.getPriorityListings()
      },
      deep: true
    },
    category: {
      handler() {
        this.getPriorityListings()
      },
      deep: true
    },
    suburb: {
      handler() {
        this.getPriorityListings()
      },
      deep: true
    }
  }
}
</script>

<style lang="scss" scoped>
.priority-listings-page {
  background: linear-gradient(180deg, #f8fafc 0%, #f5f3ff 100%);
  min-height: calc(100vh - 120px);
}

.hero-panel {
  background:
    radial-gradient(circle at 90% 10%, rgba(250, 204, 21, 0.25) 0, transparent 34%),
    linear-gradient(120deg, #312e81 0%, #4c1d95 48%, #581c87 100%);
  color: #ffffff;
}

.hero-header {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
  flex-wrap: wrap;
}

.hero-kicker {
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.78);
}

.hero-title {
  font-size: 2rem;
  line-height: 1.1;
}

.hero-subtitle {
  color: rgba(255, 255, 255, 0.82);
  max-width: 620px;
}

.hero-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.hero-filter-btn {
  color: #312e81;
  font-weight: 600;
}

.hero-create-btn {
  font-weight: 600;
}

.hero-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
}

.metric-card {
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 12px;
}

.metric-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.8);
}

.metric-value {
  font-size: 1.45rem;
  font-weight: 700;
}

.filters-card,
.table-card {
  border-color: #d9d6fe !important;
  border-radius: 14px;
}

.priority-listings-table ::v-deep thead tr th {
  font-weight: 700 !important;
  color: #4c1d95 !important;
  background: #faf5ff;
}

.name-cell {
  display: flex;
  align-items: center;
}

.name-main {
  font-weight: 600;
  color: #1f2937;
}

.name-sub {
  color: #6b7280;
  font-size: 0.72rem;
}

.email-link {
  color: #5b21b6;
  text-decoration: none;
  font-weight: 500;
}

.email-link:hover {
  text-decoration: underline;
}

@media (max-width: 960px) {
  .priority-listings-page {
    padding: 16px !important;
  }

  .hero-title {
    font-size: 1.6rem;
  }
}
</style>
